import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import importSequenceIcon from '@assets/images/import-sequence-icon.svg'

import { useModal } from '@hooks/app/modal.hook'

import { Modal } from '@components/Modal'
import { Input } from '@components/Input'

import { useCreateSequenceTemplate } from '@hooks/sequence-template/create-sequence-template.hook'

import { SequenceTaskTemplatePlanningType, SequenceTemplatePlanning } from '../../components/SequenceTemplatePlanning'

import { PopUpImportSequenceTemplate } from '../../components/PopUpImportSequenceTemplate'

import {
  ButtonImportSequence,
  Container,
  HeaderContainer,
  ImportSequenceImg,
  SequenceTemplatePlanningContainer,
} from './add-sequence-template.styled'

export const AddSequenceTemplatePage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const [name, setName] = useState<string>('')
  const [sequenceTaskTemplates, setSequenceTaskTemplates] = useState<SequenceTaskTemplatePlanningType[]>([])

  const { createSequenceTemplate } = useCreateSequenceTemplate()

  const { projectId } = useParams()

  const [importingSequenceTemplate, setImportingSequenceTemplate] = useState<boolean>(false)

  return (
    <Modal
      fullScreen
      title={t('modules_project-settings_pages_add_sequence_template_title')}
      onSubmitLabel={t('modules_project-settings_pages_add_sequence_template_button_add-sequence')}
      onClose={() => {
        hideModal('add_sequence')
      }}
      onSubmit={() => {
        if (projectId) {
          createSequenceTemplate({
            projectId,
            name,
            description: '',
            tasks: sequenceTaskTemplates.map((st) => ({
              name: st.name,
              startDay: st.startDay,
              startRow: st.startRow,
              duration: st.duration,
              workforce: st.workforce,
              tradeKey: st.tradeKey,
              companyId: st.companyId,
            })),
          })

          hideModal('add_sequence')
        }
      }}
    >
      <Container>
        <HeaderContainer>
          <Input
            className="input-sequence-name"
            autoFocus
            required
            label={t('modules_project-settings_pages_add_sequence_template_label_sequence-name')}
            placeholder={t('modules_project-settings_pages_add_sequence_template_placeholder_sequence-name')}
            value={name}
            onChange={(v) => setName(v)}
          />

          <div style={{ flex: 1 }} />

          <ButtonImportSequence onClick={() => setImportingSequenceTemplate(true)} type="button">
            <ImportSequenceImg src={importSequenceIcon} />

            {t('modules_project-settings_pages_add_sequence_template_import_sequence_label')}
          </ButtonImportSequence>

          <div style={{ width: 88 }} />
        </HeaderContainer>

        {useMemo(
          () => (
            <SequenceTemplatePlanningContainer>
              <SequenceTemplatePlanning
                sequenceTaskTemplates={sequenceTaskTemplates}
                setSequenceTaskTemplates={setSequenceTaskTemplates}
              />
            </SequenceTemplatePlanningContainer>
          ),
          [sequenceTaskTemplates],
        )}
      </Container>

      {importingSequenceTemplate && (
        <PopUpImportSequenceTemplate
          onClose={() => setImportingSequenceTemplate(false)}
          onSubmit={(sequenceTemplate) => {
            setName(sequenceTemplate.name)

            setSequenceTaskTemplates(
              sequenceTemplate.tasks.map((st) => {
                const sequence = {
                  id: st.id,
                  name: st.name,
                  description: st.description,
                  startDay: st.startDay,
                  startRow: st.startRow,
                  duration: st.duration,
                  workforce: st.workforce,
                  tradeKey: st.trade.key,
                  trade: st.trade,
                }

                if (st.__typename == 'SequenceTaskTemplate') {
                  ;(sequence as SequenceTaskTemplatePlanningType).companyId = st.company?.id
                  ;(sequence as SequenceTaskTemplatePlanningType).company = st.company
                }

                return sequence
              }),
            )

            setImportingSequenceTemplate(false)
          }}
        />
      )}
    </Modal>
  )
}
